'use client'

import React, { useEffect, useState } from 'react'
import {
  LOCALE_SELECTOR_SOURCE_COOKIE,
  LOCALE_TOOLTIP_TARGET_ID,
  USER_PREFERRED_LANGUAGE_COOKIE,
} from '@constants/localisationConstants'
import { LOCALE_CONFIG } from '@configs/localeConfig'
import styles from './localeHeaderTooltip.module.scss'

const TOOLTIP_TIMEOUT_MS = 10_000
const TOOLTIP_EXIT_MS = 800

const LocaleHeaderTooltip = ({ lang }: { lang: string }) => {
  const [position, setPosition] = useState<{
    right: number
    top: number
    arrowRight: number
    arrowMobileRight: number
  } | null>(null)
  const [isVisible, setIsVisible] = useState(true)
  const [isExiting, setIsExiting] = useState(false)
  const [selected, setSelected] = useState(false)
  const [exitTimeoutId, setExitTimeoutId] = useState<number | undefined>(undefined)

  const dismissTooltip = () => {
    if (isExiting) return
    setIsExiting(true)
    const timeoutId = window.setTimeout(() => setIsVisible(false), TOOLTIP_EXIT_MS)
    setExitTimeoutId(timeoutId)
  }

  const setPreference = () => {
    setSelected(true)
    const now = new Date()
    now.setFullYear(now.getFullYear() + 1)

    document.cookie = `${USER_PREFERRED_LANGUAGE_COOKIE}=${lang}; expires=${now.toUTCString()}; path=/`
    setTimeout(dismissTooltip, TOOLTIP_EXIT_MS)
  }

  useEffect(() => {
    document.cookie = `${LOCALE_SELECTOR_SOURCE_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`

    const setInitialPosition = () => {
      const anchor = document.getElementById(LOCALE_TOOLTIP_TARGET_ID)
      if (!anchor) {
        setPosition(null)
        return
      }

      const rect = anchor.getBoundingClientRect()
      const tooltipRight = Math.max(16, window.innerWidth - rect.right)
      setPosition({
        right: tooltipRight,
        top: rect.bottom,
        arrowRight: rect.width / 2,
        arrowMobileRight: tooltipRight + rect.width / 4,
      })
    }

    const handleScroll = () => {
      dismissTooltip()
    }

    const timeoutId = window.setTimeout(() => {
      dismissTooltip()
    }, TOOLTIP_TIMEOUT_MS)

    setInitialPosition()
    window.addEventListener('resize', setInitialPosition)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener('resize', setInitialPosition)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isExiting])

  useEffect(
    () => () => {
      if (exitTimeoutId) window.clearTimeout(exitTimeoutId)
    },
    [exitTimeoutId]
  )

  if (!position || !isVisible) return null

  return (
    <div
      className={`${styles.tooltipWrapper} ${isExiting ? styles.tooltipWrapperExit : ''}`}
      style={{ ['--top' as any]: position.top, ['--right' as any]: position.right }}>
      <div className={styles.tooltipCard} onClick={setPreference}>
        <div
          className={styles.tooltipArrow}
          style={{
            ['--arrow-right' as any]: position.arrowRight,
            ['--arrow-mobile-right' as any]: position.arrowMobileRight,
          }}
        />
        <svg className={styles.checkbox} viewBox="0 0 20 20">
          {/* Background */}
          <rect
            x="0.5"
            y="0.5"
            width="19"
            height="19"
            rx="5.5"
            fill={selected ? '#121212' : 'transparent'}
            stroke={selected ? '#121212' : '#5B5675'}
            style={{
              transition: 'all 0.2s ease',
            }}
          />

          {/* Tick */}
          <path
            d="M5.8 10L9 13L14.2 6.8"
            fill="none"
            stroke="white"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="20"
            strokeDashoffset={selected ? '0' : '20'}
            style={{
              transition: 'stroke-dashoffset 0.5s ease',
            }}
          />
        </svg>
        <p className={styles.tooltipText}>{`Always translate to ${LOCALE_CONFIG[lang].name}`}</p>
      </div>
    </div>
  )
}

export default LocaleHeaderTooltip
